import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Outcomes of /um/v1/admin/user/sync-status and /list/sync-status, in display order
export const SYNC_OUTCOMES = [
  ['MARKED_FCY', 'Now first contract year'],
  ['MARKED_NON_FCY', 'No longer first contract year'],
  ['NOT_IN_AMWAY', 'Not found in Amway (marked unregistered)'],
  ['SKIPPED_NO_CONSENT', 'Skipped: no Amway consent'],
  ['UNCHANGED', 'Unchanged'],
  ['ERROR', 'Failed']
];

const OUTCOME_LABELS = Object.fromEntries(SYNC_OUTCOMES);

// Outcomes worth listing user by user after a filtered sync
const LISTED_OUTCOMES = new Set(['MARKED_FCY', 'MARKED_NON_FCY', 'NOT_IN_AMWAY', 'ERROR']);

// One-line message for a single user's sync
export const describeSyncResult = (result) => {
  const label = OUTCOME_LABELS[result.outcome] || result.outcome;
  if (result.outcome === 'UNCHANGED') {
    return `IBO ${result.iboNumber}: status already matches Amway (first contract year: ${result.firstContractYear ? 'Yes' : 'No'}).`;
  }
  if (result.outcome === 'SKIPPED_NO_CONSENT') {
    return `IBO ${result.iboNumber}: not changed, the IBO has not given Amway consent.`;
  }
  return `IBO ${result.iboNumber}: ${label}.`;
};

export const emptySyncCounts = () => Object.fromEntries(SYNC_OUTCOMES.map(([key]) => [key, 0]));

/**
 * Progress and outcome of a sync over every user matching the View Users search and filters.
 * run: { total, processed, counts, results, running, stopping, stopped, error, filterText }
 */
const UserStatusSyncPanel = ({ run, onStop, onClose }) => {
  const listed = run.results.filter(r => LISTED_OUTCOMES.has(r.outcome));
  const percent = run.total > 0 ? Math.min(100, Math.round((run.processed / run.total) * 100)) : 100;

  let heading;
  if (run.running) {
    heading = run.stopping ? 'Stopping after this batch…' : 'Syncing status with Amway…';
  } else if (run.error) {
    heading = 'Sync stopped by an error';
  } else if (run.stopped) {
    heading = 'Sync stopped';
  } else {
    heading = 'Sync complete';
  }

  return (
    <section className="status-sync-panel" aria-label="Status sync">
      <div className="status-sync-header">
        <h3>{heading}</h3>
        {run.running ? (
          <button type="button" className="status-sync-stop" onClick={onStop} disabled={run.stopping}>
            Stop
          </button>
        ) : (
          <button type="button" className="status-sync-close" onClick={onClose} aria-label="Close sync results">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>

      {run.filterText && <p className="status-sync-scope">{run.filterText}</p>}

      <div
        className="status-sync-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={run.total}
        aria-valuenow={run.processed}
      >
        <div className="status-sync-progress-bar" style={{ width: `${percent}%` }} />
      </div>
      <p className="status-sync-count">
        {run.processed.toLocaleString()} of {run.total.toLocaleString()} users checked
      </p>

      {run.error && <p className="status-sync-error">{run.error}</p>}

      <div className="status-sync-counts">
        {SYNC_OUTCOMES.map(([key, label]) => (
          <div key={key} className={`status-sync-count-card outcome-${key.toLowerCase()}`} role="group" aria-label={label}>
            <span className="user-summary-value">{(run.counts[key] || 0).toLocaleString()}</span>
            <span className="user-summary-label">{label}</span>
          </div>
        ))}
      </div>

      {listed.length > 0 && (
        <details className="status-sync-details" open={!run.running && listed.length <= 20}>
          <summary>Changed or failed users ({listed.length})</summary>
          <table className="status-sync-table">
            <thead>
              <tr>
                <th>IBO Number</th>
                <th>Result</th>
                <th>Amway status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {listed.map(r => (
                <tr key={r.iboNumber}>
                  <td>{r.iboNumber}</td>
                  <td>{OUTCOME_LABELS[r.outcome] || r.outcome}</td>
                  <td>{r.amwayStatus || '—'}</td>
                  <td>{r.outcome === 'ERROR' ? r.error : (r.paymentEligible ? `Payment eligible: ${r.paymentEligible}` : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
};

export default UserStatusSyncPanel;
